import { store } from "../../app/store";
import { Logger } from "../../tools/Logger";
import { onClose, onError, onOpen } from "./aircraftSlice";
import {
  selectAircraftConnectionById,
  selectClientId,
} from "./aircraftSelectors";
import { selectUsername } from "../session/sessionSlice";
import { isDirectTelemetry } from "./types";

export class SingleSocket {
  private TAG: string = "SingleSocket";
  private ws: WebSocket | null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private retryTimeout: NodeJS.Timeout | null = null;
  private retryCount: number = 0;
  private retryInterval: number = 10000;
  private shortRetryInterval: number = 2000;
  private ip: string;
  private token: string;
  private onmessage: (event: MessageEvent) => void;
  private clientId: string;
  private uavId: string;
  private index: 0 | 1;
  private latency: number = 0;
  private latencyThreshold: number = 3000;
  private latencyRetryThreshold: number = 20000;
  private user: string = "";
  private latePacketCount: number = 0;
  private port: string = "5000";

  constructor(
    ip: string,
    token: string,
    onmessage: (event: MessageEvent) => void,
    uavId: string,
    port: string,
    index: 0 | 1
  ) {
    this.ip = ip;
    this.token = token;
    this.onmessage = onmessage;
    this.uavId = uavId;
    this.clientId = selectClientId(store.getState());
    this.index = index;
    this.user = selectUsername(store.getState()) || "";
    this.port = port;
    this.ws = new WebSocket(this.getUrl());
    this.createWebSocketEventHandlers(this.ws, this.ip);
    const searchParams = new URLSearchParams(window.location.search);
    const latencyThresholdMs = searchParams.get("latencyThresholdMs");

    if (latencyThresholdMs) {
      const parsedLatencyThreshold = Number(latencyThresholdMs);
      if (!isNaN(parsedLatencyThreshold) && parsedLatencyThreshold > 0) {
        this.latencyThreshold = parsedLatencyThreshold;
      }
    }
    this.heartbeatInterval = setInterval(() => {
      this.send("heartbeat");
    }, 1000);
  }

  public getReadyState = () => {
    return this.ws?.readyState;
  };

  private getUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const sslParam = searchParams.get("ssl");
    const protocol = sslParam === "false" ? "ws" : "wss";
    return `${protocol}://${this.ip}${this.port}/ws/telemetry?token=${this.token}&uuid=${this.clientId}&user=${this.user}`;
  };

  public pingPong = (id: string) => {
    try {
      const pilot_info = {
        p1: {
          location: {
            latitude: 0,
            longitude: 0,
          },
        },
      };

      const pingObj = {
        type: "PING",
        id,
        pilot_info,
      };
      this.send(JSON.stringify(pingObj));
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
  };

  public getLogData = () => {
    return {
      uavId: this.uavId,
      uav_id: this.uavId,
      ip: this.ip,
      clientId: this.clientId,
      socketIndex: this.index,
      user: this.user,
      latency: this.latency,
      socketReadyState: this.ws?.readyState,
      retries: this.retryCount,
    };
  };
  public recordLatency = (id: string, timestamp?: number) => {
    if (!timestamp) {
      return;
    }
    const now = Date.now();
    const latency = now - timestamp;
    this.latency = latency;

    if (this.latency > this.latencyThreshold) {
      Logger.log(
        "info",
        this.TAG,
        `latency ${id} ${this.latency} ${this.ip}`,
        {
          ...this.getLogData(),
        },
        true
      );
    }
  };

  public destroyConnection = () => {
    try {
      if (this.ws) {
        this.ws?.close?.();
        this.ws = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
    try {
      if (this.retryTimeout) {
        clearInterval(this.retryTimeout);
        this.retryTimeout = null;
      }
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    } catch (err: any) {
      Logger.log("error", this.TAG, err?.message, {
        ...this.getLogData(),
        err,
      });
    }
  };

  private getRetryInterval = () => {
    const retryInterval = (this.retryCount + 1) * this.shortRetryInterval;
    if (retryInterval < this.retryInterval) {
      return retryInterval;
    } else {
      return this.retryInterval;
    }
  };

  private createWebSocketEventHandlers(ws: WebSocket, ip: string) {
    ws.onopen = () => {
      Logger.log(
        "info",
        this.TAG,
        `onopen ${ip}`,
        {
          ...this.getLogData(),
        },
        true
      );
      store.dispatch(onOpen({ id: this.uavId, ip }));

      this.retryCount = 0;
    };
    ws.onmessage = (event) => {
      this.onmessage(event);

      try {
        const data = JSON.parse(event?.data);

        if (isDirectTelemetry(data)) {
          try {
            this.pingPong(data.id);
          } catch (err: any) {
            Logger.log("error", this.TAG, err?.message, {
              ...this.getLogData(),
              err,
            });
          }
          try {
            data.payload.uav_time &&
              this.recordLatency(data.id, data.payload.uav_time);
          } catch (err: any) {
            Logger.log("error", this.TAG, err?.message, {
              ...this.getLogData(),
              err,
            });
          }
        } else {
        }
      } catch (err: any) {
        Logger.log("error", this.TAG, err?.message, {
          ...this.getLogData(),
          err,
        });
      }
    };
    ws.onclose = (e) => {
      Logger.log(
        "info",
        this.TAG,
        `on close: ${this.ip} ${this.uavId} ${e.code} ${this.getReadyString()}`,
        {
          ...this.getLogData(),
        }
      );

      let retry = false;

      const shouldRetry = [1001, 1006, 1012, 1013].includes(e.code);

      if (shouldRetry) {
        const retryInterval = this.getRetryInterval();
        Logger.log(
          "info",
          this.TAG,
          `${this.ip} ${this.uavId} ${e.code} Retrying in ${
            retryInterval / 1000
          } seconds`,
          {
            ...this.getLogData(),
          }
        );

        retry = true;
        this.retryTimeout = setTimeout(() => {
          this.retryConnection();
          this.retryCount++;
        }, retryInterval);
      } else if ([1000, 1005].includes(e.code)) {
        Logger.info(
          this.TAG,
          `${this.ip} ${this.uavId} ${e.code} Connection closed normally or without status, not retrying`,
          {
            ...this.getLogData(),
          }
        );
      } else {
        Logger.warn(
          this.TAG,
          `Not retrying after close code ${e.code} or max retries reached ${this.ip}`
        );
      }
      Logger.log(
        "info",
        `on close: ${this.ip} ${this.uavId} ${
          e.code
        } ${this.getReadyString()} ${retry ? "retrying" : "not retrying"}`,
        "SingleSocket",
        {
          ...this.getLogData(),
        }
      );
      store.dispatch(
        onClose({
          id: this.uavId,
          socket: {
            index: this.index,
            status: `${this.getReadyString()}`,
            active: false,
          },
        })
      );
    };

    ws.onerror = (event) => {
      Logger.error(this.TAG, event);
      store.dispatch(
        onError({
          id: this.uavId,
          socket: {
            index: this.index,
            status: `${this.getReadyString()} onError`,
            active: false,
          },
        })
      );
    };
  }

  private retryConnection() {
    Logger.info(this.TAG, `retryConnection ${this.ip}`);
    this.ws = null;
    this.ws = new WebSocket(this.getUrl());
    this.createWebSocketEventHandlers(this.ws, this.ip);
  }

  private getReadyString = () => {
    if (this.ws === null) return "No socket";
    if (this.ws?.readyState === this.ws?.CONNECTING) {
      return "Connecting";
    } else if (this.ws?.readyState === this.ws?.OPEN) {
      return "Open";
    } else if (this.ws?.readyState === this.ws?.CLOSING) {
      return "Closing";
    } else if (this.ws?.readyState === this.ws?.CLOSED) {
      return "Closed";
    }
  };

  public send = (data: string) => {
    if (this.ws && this.ws.readyState === this.ws.OPEN) {
      this.ws.send(data);
    } else {
      Logger.trace(this.TAG, `socket not open ${this.ip}`);
    }
  };
}
